// The one setting: whether the board sounds, on by default, as
// `settings/sounds.json`. Pure parsing, and a small store over the storage
// API that the listener reads and the Settings section writes.
import type { Storage } from '@chess/api-storage';

export const SOUNDS_FILE = 'sounds.json';

export interface SoundSettings {
  enabled: boolean;
}

export const DEFAULT_SOUND_SETTINGS: SoundSettings = { enabled: true };

/** The settings from the file's text; the defaults for no file or a broken one. */
export function parseSoundSettings(text: string | undefined): SoundSettings {
  if (!text) return { ...DEFAULT_SOUND_SETTINGS };
  try {
    const raw: unknown = JSON.parse(text);
    const enabled = typeof raw === 'object' && raw !== null && 'enabled' in raw ? (raw as { enabled: unknown }).enabled : undefined;
    return { enabled: typeof enabled === 'boolean' ? enabled : DEFAULT_SOUND_SETTINGS.enabled };
  } catch {
    return { ...DEFAULT_SOUND_SETTINGS };
  }
}

export type SoundStorage = Pick<Storage, 'readText' | 'write'>;

export class SoundSettingsStore {
  private current: SoundSettings = { ...DEFAULT_SOUND_SETTINGS };
  private loaded?: Promise<SoundSettings>;
  private listeners = new Set<(s: SoundSettings) => void>();
  private readonly storage: Promise<SoundStorage>;

  constructor(storage: Promise<SoundStorage>) {
    this.storage = storage;
  }

  /** The settings as last read or saved; the defaults until `load` resolves. */
  get(): SoundSettings {
    return this.current;
  }

  /** Reads the file once; later calls return the same promise. */
  load(): Promise<SoundSettings> {
    this.loaded ??= this.storage
      .then((s) => s.readText(SOUNDS_FILE))
      .then((text) => this.set(parseSoundSettings(text)));
    return this.loaded;
  }

  async save(next: SoundSettings): Promise<SoundSettings> {
    const s = await this.storage;
    await s.write(SOUNDS_FILE, JSON.stringify(next));
    return this.set(next);
  }

  subscribe(listener: (s: SoundSettings) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private set(next: SoundSettings): SoundSettings {
    this.current = next;
    for (const l of this.listeners) l(next);
    return next;
  }
}
